import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { FilterDef, FilterValues } from './chip-filters.types';
import ValuePicker from './value-picker.component';
import styles from './filter-drawer.module.scss';

interface FilterDrawerProps {
  defs: FilterDef[];
  value: FilterValues;
  isOpen: boolean;
  onClose: () => void;
  onChange: (next: FilterValues) => void;
  /** Called on every draft change so the parent can react (e.g. fetch async options) */
  onDraftChange?: (draft: FilterValues) => void;
}

function getDisplayValue(
  def: FilterDef,
  val: string | string[] | undefined,
  t: (k: string, o?: Record<string, unknown>) => string
): string | null {
  if (!val || (Array.isArray(val) && val.length === 0)) return null;
  if (Array.isArray(val)) {
    if (val.length === 1) return def.options.find((o) => o.value === val[0])?.label ?? val[0];
    return t('chip-filters.selected', { count: val.length });
  }
  return def.options.find((o) => o.value === val)?.label ?? val;
}

export default function FilterDrawer({
  defs,
  value,
  isOpen,
  onClose,
  onChange,
  onDraftChange
}: FilterDrawerProps) {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<FilterValues>(value);

  // Sync draft from value when drawer opens; reset accordion
  useEffect(() => {
    if (isOpen) {
      setDraft(value);
      setOpenKey(null);
    }
  }, [isOpen]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const draftActiveCount = defs.filter((def) => {
    const v = draft[def.key];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== '';
  }).length;

  const handleDraftChange = (key: string, next: string | string[] | undefined) => {
    setDraft((prev) => {
      let updated = { ...prev, [key]: next };
      // Auto-clear any filter that becomes disabled after this change
      for (const def of defs) {
        if (def.key !== key && def.disabledFn?.(updated) && updated[def.key] !== undefined) {
          updated = { ...updated, [def.key]: undefined };
        }
      }
      onDraftChange?.(updated);
      return updated;
    });
  };

  const handleClearAll = () => {
    const cleared: FilterValues = {};
    for (const def of defs) cleared[def.key] = undefined;
    setDraft(cleared);
    onDraftChange?.(cleared);
  };

  const handleApply = () => {
    onChange(draft);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.backdrop}
            aria-hidden="true"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          />

          {/* Bottom sheet */}
          <motion.div
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={t('general.filters')}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320, mass: 0.8 }}
          >
            {/* Drag handle */}
            <div className={styles.handle} />

            {/* Header */}
            <div className={styles.header}>
              <span className={styles.headerTitle}>{t('general.filters')}</span>
              {draftActiveCount > 0 && <span className={styles.badge}>{draftActiveCount}</span>}
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label={t('chip-filters.done')}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                  <path
                    d="M1 1L10 10M10 1L1 10"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Accordion filters */}
            <div className={styles.body}>
              {defs.map((def) => {
                const isItemOpen = openKey === def.key;
                const displayValue = getDisplayValue(def, draft[def.key], t);
                const resolvedDisabled = def.disabledFn ? def.disabledFn(draft) : def.disabled;
                const isDisabled = resolvedDisabled && !displayValue;

                return (
                  <div key={def.key} className={styles.filterItem}>
                    <div className={styles.filterRow}>
                      <button
                        type="button"
                        className={clsx(
                          styles.filterTrigger,
                          isDisabled && styles.filterTriggerDisabled
                        )}
                        onClick={() => !isDisabled && setOpenKey(isItemOpen ? null : def.key)}
                        aria-expanded={isItemOpen}
                        aria-disabled={isDisabled}
                        title={isDisabled ? def.disabledHint : undefined}
                      >
                        <span className={styles.filterIcon}>{def.icon}</span>
                        <span className={styles.filterLabelWrap}>
                          <span className={styles.filterLabel}>{def.label}</span>
                          {displayValue && (
                            <span className={styles.filterValue}>{displayValue}</span>
                          )}
                        </span>
                        <motion.span
                          className={styles.filterCaret}
                          animate={{ rotate: isItemOpen ? 180 : 0 }}
                          transition={{ duration: 0.18, ease: 'easeInOut' }}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2 4L6 8L10 4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </motion.span>
                      </button>

                      {displayValue && !isDisabled && (
                        <button
                          type="button"
                          className={styles.filterItemClearBtn}
                          onClick={() => handleDraftChange(def.key, undefined)}
                          aria-label={`Limpiar ${def.label}`}
                        >
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 9 9"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 1L8 8M8 1L1 8"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <AnimatePresence initial={false}>
                      {isItemOpen && (
                        <motion.div
                          className={styles.filterOptions}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                          style={{ overflow: 'hidden' }}
                        >
                          <ValuePicker
                            def={def.optionsFn ? { ...def, options: def.optionsFn(draft) } : def}
                            current={draft[def.key]}
                            onApply={(next) => handleDraftChange(def.key, next)}
                            onClose={() => setOpenKey(null)}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              {draftActiveCount > 0 && (
                <button type="button" className={styles.clearBtn} onClick={handleClearAll}>
                  {t('chip-filters.clearAll', { count: draftActiveCount })}
                </button>
              )}
              <button type="button" className={styles.applyBtn} onClick={handleApply}>
                {t('chip-filters.apply')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
